import React from "react";
import { Helmet } from "react-helmet-async";
import { LEADERSHIP, COMPANY, SOLUTIONS, FAQ } from "@/data/site";

// -----------------------------------------------------------------------------
// SEO helper, emits <title>, meta description, canonical, Open Graph, Twitter,
// and JSON-LD schema. Pass the pageType to opt into structured data blocks that
// only apply to that page (person / service / article / faq).
// -----------------------------------------------------------------------------
const SITE = "https://consiliumpro.co";
const BRAND = "Consilium Pro Workforce Solutions";
const OG_IMAGE = "https://customer-assets-wrfwihn1.emergentagent.net/job_consilium-pro-launch/artifacts/social-og.png";

// Site-wide Organization + ProfessionalService schema, emitted on every page.
const ORG_JSON = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["Organization", "ProfessionalService"],
      "@id": `${SITE}/#org`,
      name: BRAND,
      legalName: `${BRAND}, LLC`,
      url: SITE,
      logo: `${SITE}/logo512.png`,
      email: COMPANY.email,
      telephone: COMPANY.phone,
      address: {
        "@type": "PostalAddress",
        addressLocality: "Atlanta",
        addressRegion: "GA",
        addressCountry: "US",
      },
      areaServed: { "@type": "Country", name: "United States" },
      knowsAbout: [
        "Workforce Consulting","People Operations","Fractional HR","Government Contractor Readiness",
        "Technology Strategy & Assessment","Leadership Development","Public Affairs","Operational Readiness",
      ],
      slogan: "Principal-led. Multidisciplinary. Scalable. Practitioner-driven.",
    },
  ],
};

const PEOPLE_JSON = () => ({
  "@context": "https://schema.org",
  "@graph": LEADERSHIP.map((p) => ({
    "@type": "Person",
    "@id": `${SITE}/team#${p.initials.toLowerCase()}`,
    name: p.name,
    jobTitle: p.role,
    worksFor: { "@id": `${SITE}/#org` },
    ...(p.image ? { image: p.image } : {}),
    knowsAbout: p.expertise || p.areas,
  })),
});

const SERVICES_JSON = () => ({
  "@context": "https://schema.org",
  "@graph": SOLUTIONS.map((s) => ({
    "@type": "Service",
    "@id": `${SITE}/solutions#${s.slug}`,
    name: s.title,
    description: s.tagline,
    provider: { "@id": `${SITE}/#org` },
    serviceType: s.delivery_models?.join(", "),
  })),
});

const FAQ_JSON = () => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const ARTICLE_JSON = (article) => ({
  "@context": "https://schema.org",
  "@type": "Article",
  headline: article.title,
  description: article.desc,
  datePublished: article.date,
  ...(article.image ? { image: article.image } : {}),
  author: { "@type": "Person", name: article.author, jobTitle: article.author_role },
  publisher: { "@id": `${SITE}/#org` },
});

// Breadcrumbs, pass an array of { name, path } from root → current page.
// The current page's `path` can be omitted (Google infers it from the current URL),
// but we include it so the schema is fully-qualified.
const BREADCRUMB_JSON = (breadcrumbs) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: breadcrumbs.map((b, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: b.name,
    ...(b.path ? { item: `${SITE}${b.path}` } : {}),
  })),
});

export default function SEO({
  title,
  description,
  path = "/",
  image = OG_IMAGE,
  pageType = "page",   // page | people | services | faq | article
  article,             // required when pageType === "article"
  breadcrumbs,         // optional: [{ name, path }], emits BreadcrumbList JSON-LD
  noIndex = false,
}) {
  const canonical = `${SITE}${path}`;
  const fullTitle = title ? `${title} · ${BRAND}` : BRAND;

  return (
    <Helmet prioritizeSeoTags>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      <link rel="canonical" href={canonical} />
      {noIndex && <meta name="robots" content="noindex,nofollow" />}
      {/* Open Graph */}
      <meta property="og:site_name" content={BRAND} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={pageType === "article" ? "article" : "website"} />
      {image && <meta property="og:image" content={image} />}
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
      {/* JSON-LD */}
      <script type="application/ld+json">{JSON.stringify(ORG_JSON)}</script>
      {pageType === "people"   && <script type="application/ld+json">{JSON.stringify(PEOPLE_JSON())}</script>}
      {pageType === "services" && <script type="application/ld+json">{JSON.stringify(SERVICES_JSON())}</script>}
      {pageType === "faq"      && <script type="application/ld+json">{JSON.stringify(FAQ_JSON())}</script>}
      {pageType === "article" && article && <script type="application/ld+json">{JSON.stringify(ARTICLE_JSON(article))}</script>}
      {breadcrumbs && breadcrumbs.length > 0 && <script type="application/ld+json">{JSON.stringify(BREADCRUMB_JSON(breadcrumbs))}</script>}
    </Helmet>
  );
}
